import React from "react";
import { Box, Button, Divider } from "@material-ui/core";

import {
  ActionButton,
  AvailabilityDuration,
  AvailabilityLanguage,
  AvailabilityReviewRating,
  ExperienceItineraryDetails,
} from "halifax";
import {
  ActivitiesDetailsProperties,
  ActivitiesEntryTypeEnum,
  CLICKED_SIMILAR_ACTIVITIES,
  ExperienceAvailability,
} from "redmond";
import { getExperiencesShopQuery } from "@capone/common";

import styles from "./styles.module.scss";
import { CONTINUE_TO_CHECKOUT, VIEW_DETAILS } from "../../../../textConstants";
import { PATH_SHOP } from "../../../../../../utils/paths";
import { trackEvent } from "../../../../../../api/v0/analytics/trackEvent";

export interface IExperiencesCompareCardProps {
  experiencesAvailabilityInfo: ExperienceAvailability;
  selectedExperienceIdValue: string;
  fromDate: Date | null;
  untilDate: Date | null;
  closeModal: () => void;
  locationLabel: string | null;
  entryType?: ActivitiesEntryTypeEnum;
  entryTypesAll?: ActivitiesEntryTypeEnum[];
  similarIndex?: number;
  activityDetailsProperties?: {
    properties: ActivitiesDetailsProperties;
    encryptedProperties: string[];
  };
}

export const ExperiencesCompareCard = (
  props: IExperiencesCompareCardProps
): React.ReactElement => {
  const {
    experiencesAvailabilityInfo,
    selectedExperienceIdValue,
    fromDate,
    untilDate,
    closeModal,
    locationLabel,
    entryType,
    entryTypesAll,
    similarIndex,
    activityDetailsProperties,
  } = props;

  const {
    name,
    rating,
    duration,
    durationFromMinutes,
    durationToMinutes,
    bestPrice,
    coverImage,
    id,
    languageGuides,
    itinerary,
  } = experiencesAvailabilityInfo;

  const isMainExpCard = selectedExperienceIdValue === id?.value;

  return (
    <Box className={styles["card-container"]}>
      <Box className={styles["info-container"]}>
        <img
          className={styles["cover-img-thumbnail"]}
          src={coverImage}
          alt=""
        />
        <Box className={styles["experience-details"]}>
          <Box className={styles["experience-name"]}>{name}</Box>
          <Box className={styles["experience-other-details"]}>
            <AvailabilityReviewRating
              reviewCount={rating.numberOfReviews}
              scaledScore={rating.reviewAverage}
              shortReviews
            />
            {duration && (
              <>
                <Divider
                  className={
                    styles["experiences-shop-overview-subtext-dividers"]
                  }
                  orientation="vertical"
                />
                <AvailabilityDuration
                  duration={duration}
                  durationFromMinutes={durationFromMinutes}
                  durationToMinutes={durationToMinutes}
                />
              </>
            )}
            {languageGuides && languageGuides.length > 0 && (
              <>
                <Divider
                  className={
                    styles["experiences-shop-overview-subtext-dividers"]
                  }
                  orientation="vertical"
                />
                <AvailabilityLanguage languages={languageGuides} />
              </>
            )}
          </Box>
        </Box>
      </Box>
      {isMainExpCard ? (
        <ActionButton
          className={styles["checkout-cta-button"]}
          onClick={() => {
            closeModal();
            setTimeout(() => {
              document
                .getElementsByClassName("continue-button")[0]
                ?.scrollIntoView({ behavior: "smooth", block: "end" });
            }, 300);
          }}
          message={CONTINUE_TO_CHECKOUT}
        />
      ) : (
        <Button
          variant="outlined"
          color="primary"
          className={styles["view-details-button"]}
          onClick={() => {
            if (id) {
              if (activityDetailsProperties) {
                trackEvent({
                  eventName: CLICKED_SIMILAR_ACTIVITIES,
                  properties: {
                    ...activityDetailsProperties.properties,
                    product_id_clicked: id.value,
                    product_name_clicked: name,
                    price_clicked: bestPrice?.fiat?.value,
                    duration_clicked: duration,
                    rating_clicked: rating?.reviewAverage,
                    product_index_clicked:
                      similarIndex !== undefined
                        ? similarIndex + 1 // 1-based index for product metrics
                        : undefined,
                  },
                  encryptedProperties:
                    activityDetailsProperties.encryptedProperties,
                });
              }
              window.open(
                `${PATH_SHOP}${getExperiencesShopQuery(
                  id,
                  fromDate,
                  untilDate,
                  locationLabel,
                  undefined,
                  undefined,
                  ActivitiesEntryTypeEnum.SIMILAR_ACTIVITIES,
                  entryTypesAll ?? (entryType ? [entryType] : [])
                )}`,
                "_blank"
              );
            }
          }}
        >
          {VIEW_DETAILS}
        </Button>
      )}
      <ExperienceItineraryDetails itinerary={itinerary} />
    </Box>
  );
};
